import { useState, useCallback, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { httpCheckoutAPI } from 'V2/Services/httpCheckoutAPI';
import { CHECKOUT_SESSION_KEYS } from 'V2/Checkout/constants';

export type BindDecision =
  | 'idle'
  | 'submitting'
  | 'polling'
  | 'policy_bound'
  | 'payment_error'
  | 'error'
  | 'quote_change'
  | 'uw_hard_stop';

interface UseBindSubmitResult {
  submit: (paymentToken: string) => Promise<void>;
  bindStatus: BindDecision;
  bindErrors: string[];
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 60;

export const useBindSubmit = (
  appId: string,
  paymentPlanId: string
): UseBindSubmitResult => {
  const history = useHistory();
  const [bindStatus, setBindStatus] = useState<BindDecision>('idle');
  const [bindErrors, setBindErrors] = useState<string[]>([]);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const poll = useCallback(
    async (bindAttemptId: string, attempt = 0) => {
      if (attempt >= MAX_POLL_ATTEMPTS) {
        setBindStatus('error');
        setBindErrors([
          'Verification is taking longer than expected. Please contact your agent.',
        ]);
        return;
      }

      try {
        const response = await httpCheckoutAPI.get({
          url: `/bindapi/insurance_applications/${appId}/bind_attempts/${bindAttemptId}/status`,
        });
        const { decision } = response.data as { decision: string };

        if (decision === 'pending') {
          pollRef.current = setTimeout(
            () => poll(bindAttemptId, attempt + 1),
            POLL_INTERVAL_MS
          );
          return;
        }

        stopPolling();

        switch (decision) {
          case 'policy_bound':
            setBindStatus('policy_bound');
            history.push('/checkout/confirmation');
            break;
          case 'payment_error':
            setBindStatus('payment_error');
            setBindErrors([
              'Payment could not be processed. Please check your card details and try again.',
            ]);
            break;
          case 'quote_change':
            setBindStatus('quote_change');
            break;
          case 'uw_hard_stop':
            setBindStatus('uw_hard_stop');
            break;
          default:
            setBindStatus('error');
            setBindErrors(['Something went wrong. Please contact your agent.']);
        }
      } catch {
        stopPolling();
        setBindStatus('error');
        setBindErrors(['Something went wrong. Please try again.']);
      }
    },
    [appId, history, stopPolling]
  );

  const submit = useCallback(
    async (paymentToken: string) => {
      setBindErrors([]);
      setBindStatus('submitting');

      try {
        const quoteId =
          sessionStorage.getItem(CHECKOUT_SESSION_KEYS.quoteId) ?? '';

        const response = await httpCheckoutAPI.post({
          url: `/bindapi/insurance_applications/${appId}/bind`,
          data: {
            paymentToken,
            paymentPlanId,
            paymentMethod: 'credit_card',
            quoteId,
          },
        });
        const { bindAttemptId } = response.data as { bindAttemptId: string };
        setBindStatus('polling');
        void poll(bindAttemptId);
      } catch (err: any) {
        if (err.response?.status === 409) {
          setBindStatus('quote_change');
        } else {
          setBindStatus('error');
          setBindErrors(['Something went wrong. Please try again.']);
        }
      }
    },
    [appId, paymentPlanId, poll]
  );

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  return { submit, bindStatus, bindErrors };
};
